/**
 * IndexedDB Storage Layer
 *
 * Implements:
 * - REQ-011: Agent Identity Persistence (Web)
 * - REQ-015: Pending Queue (Web)
 *
 * Uses idb-keyval for simple key-value storage.
 */

import { get, set, del, keys, clear } from "idb-keyval";

export interface PendingAppend {
  localId: string;
  payload: { type: "append"; forms: string[] };
  sig: string;
  pubkey: string;
  createdAt: string;
}

export interface CachedPlan {
  planId: string;
  token: string;
  entries: unknown[];
  lastSeq: number;
  lastSyncedAt: string;
}

// Identity storage
export async function getIdentity(): Promise<{ privateKey: Uint8Array; publicKey: Uint8Array } | null> {
  const identity = await get<{ privateKey: Uint8Array; publicKey: Uint8Array }>("hence-identity");
  return identity ?? null;
}

export async function setIdentity(identity: { privateKey: Uint8Array; publicKey: Uint8Array; createdAt: string }): Promise<void> {
  await set("hence-identity", identity);
}

// Pending queue storage
export async function getPendingAppends(planToken: string): Promise<PendingAppend[]> {
  return (await get(`pending:${planToken}`)) || [];
}

export async function addPendingAppend(planToken: string, append: PendingAppend): Promise<void> {
  const pending = await getPendingAppends(planToken);
  pending.push(append);
  await set(`pending:${planToken}`, pending);
}

export async function removePendingAppend(planToken: string, localId: string): Promise<void> {
  const pending = await getPendingAppends(planToken);
  const filtered = pending.filter((p) => p.localId !== localId);
  await set(`pending:${planToken}`, filtered);
}

export async function clearPendingAppends(planToken: string): Promise<void> {
  await del(`pending:${planToken}`);
}

// Plan cache storage
export async function getCachedPlan(planToken: string): Promise<CachedPlan | null> {
  const plan = await get<CachedPlan>(`plan:${planToken}`);
  return plan ?? null;
}

export async function setCachedPlan(planToken: string, plan: CachedPlan): Promise<void> {
  await set(`plan:${planToken}`, plan);
}

export async function clearCachedPlan(planToken: string): Promise<void> {
  await del(`plan:${planToken}`);
}

// Get all cached plan tokens
export async function getCachedPlanTokens(): Promise<string[]> {
  const allKeys = await keys();
  return allKeys
    .filter((key) => typeof key === "string" && key.startsWith("plan:"))
    .map((key) => (key as string).replace("plan:", ""));
}

// Clear all storage
export async function clearAllStorage(): Promise<void> {
  await clear();
}

// ============================================================================
// Recent Plans (localStorage)
// ============================================================================

const RECENT_PLANS_KEY = "hence-recent-plans";
const MAX_RECENT_PLANS = 15;

export interface RecentPlan {
  token: string;           // Full capability token
  name: string;            // Auto-extracted from plan content
  petname?: string;        // User-set nickname (overrides name when set)
  lastAccessed: number;    // Unix timestamp (ms)
  scope: "read" | "append" | "admin";
}

interface LogEntry {
  seq: number;
  ts: string;
  agent: string;
  forms: string[];
  sig: string;
}

/**
 * Extract a human-readable name from plan entries.
 * Priority: plan meta description → header comment → first task description → "Untitled Plan"
 */
export function extractPlanName(entries: LogEntry[]): string {
  for (const entry of entries) {
    for (const form of entry.forms) {
      // Check for plan meta with description
      // Format: (meta plan :description "...")
      const planMetaMatch = form.match(/\(meta\s+plan\s+[^)]*:description\s+"([^"]+)"/);
      if (planMetaMatch) {
        return planMetaMatch[1];
      }
    }
  }

  for (const entry of entries) {
    for (const form of entry.forms) {
      // Check for header comment
      // Format: ; Comment at start of plan
      if (form.startsWith(";")) {
        const comment = form.slice(1).trim();
        if (comment.length > 0 && comment.length < 100) {
          return comment;
        }
      }
    }
  }

  for (const entry of entries) {
    for (const form of entry.forms) {
      // Check for first task meta with description
      // Format: (meta task-id :description "...")
      const taskMetaMatch = form.match(/\(meta\s+[^\s)]+\s+[^)]*:description\s+"([^"]+)"/);
      if (taskMetaMatch) {
        return taskMetaMatch[1];
      }
    }
  }

  return ""; // Will use token prefix as fallback in UI
}

/**
 * Get all recent plans from localStorage
 */
export function getRecentPlans(): RecentPlan[] {
  try {
    const stored = localStorage.getItem(RECENT_PLANS_KEY);
    if (!stored) return [];
    return JSON.parse(stored) as RecentPlan[];
  } catch {
    return [];
  }
}

/**
 * Add or update a recent plan, moving it to the top of the list.
 * Trims list to MAX_RECENT_PLANS.
 */
export function addRecentPlan(plan: Omit<RecentPlan, "lastAccessed">): void {
  try {
    const plans = getRecentPlans();

    // Find existing plan by token
    const existingIndex = plans.findIndex((p) => p.token === plan.token);

    const newPlan: RecentPlan = {
      ...plan,
      lastAccessed: Date.now(),
      // Preserve petname if it exists
      petname: existingIndex >= 0 ? plans[existingIndex].petname : undefined,
    };

    // Remove existing if found
    if (existingIndex >= 0) {
      plans.splice(existingIndex, 1);
    }

    // Add to front
    plans.unshift(newPlan);

    // Trim to max
    const trimmed = plans.slice(0, MAX_RECENT_PLANS);

    localStorage.setItem(RECENT_PLANS_KEY, JSON.stringify(trimmed));
  } catch {
    // Silently fail if localStorage is unavailable
  }
}

/**
 * Update the petname for a plan.
 * Pass empty string or undefined to clear the petname.
 */
export function updatePlanPetname(token: string, petname: string | undefined): void {
  try {
    const plans = getRecentPlans();
    const plan = plans.find((p) => p.token === token);
    if (!plan) return;

    if (petname && petname.trim()) {
      plan.petname = petname.trim();
    } else {
      delete plan.petname;
    }

    localStorage.setItem(RECENT_PLANS_KEY, JSON.stringify(plans));
  } catch {
    // Silently fail if localStorage is unavailable
  }
}

/**
 * Remove a plan from the recent list.
 */
export function removeRecentPlan(token: string): void {
  try {
    const plans = getRecentPlans();
    const filtered = plans.filter((p) => p.token !== token);
    localStorage.setItem(RECENT_PLANS_KEY, JSON.stringify(filtered));
  } catch {
    // Silently fail if localStorage is unavailable
  }
}
